import { getLadderStatus } from "./ladder-progress";
export const getCompletedAwards = (
  awards,
  allCareerClimbs,
  ladderProgress,
  courses = [],
) => {
  return awards.filter((award) =>
    checkAwardCompletion(award, allCareerClimbs, ladderProgress, courses),
  );
};

export const checkAwardCompletion = (
  award,
  allCareerClimbs,
  ladderProgress,
  courses = [],
) => {
  const climbsForAward = getClimbsForAward(award, allCareerClimbs, courses);

  // If no climbs are required for this award, it should not be marked as complete
  if (climbsForAward.length === 0) {
    return false;
  }

  return climbsForAward.every((climbId) =>
    hasCompletedClimb(climbId, allCareerClimbs, ladderProgress),
  );
};

export const hasCompletedClimb = (climbId, allCareerClimbs, ladderProgress) => {
  const careerClimb = allCareerClimbs.find(
    (climb) => String(climb.id) === String(climbId),
  );
  if (!careerClimb) return false;
  if (!ladderProgress) return false;
  const ladders = careerClimb.ladders;
  return ladders.every(
    (ladder) => getLadderStatus(ladder, ladderProgress) === "COMPLETED",
  );
};

// Returns all the climb ID's that match the award type
export const getClimbsForAward = (award, allCareerClimbs, courses = []) => {
  const { awardRule } = award;
  const { requiredClimbs, requiredCourses } = award;

  switch (awardRule) {
    case "all_climbs_in_plan":
      return getAllClimbsInPlan(award.productPlan?.[0]?.code, allCareerClimbs);
    case "career_climb_list":
      return requiredClimbs || [];
    case "all_climbs_in_course":
      return getAllClimbsInCourses(requiredCourses, courses);
    default:
      console.error("Unknown award rule ", awardRule);
      return [];
  }
};

const getAllClimbsInCourses = (courseIds, courses) => {
  if (
    !courseIds ||
    courseIds.length === 0 ||
    !courses ||
    courses.length === 0
  ) {
    return [];
  }

  return courseIds.flatMap((courseId) => {
    const course = courses.find((c) => String(c.id) === String(courseId));
    if (!course || !course.climbs) return [];
    return course.climbs.map((climb) => climb.id).filter(Boolean);
  });
};

const getAllClimbsInPlan = (planCode, allCareerClimbs) => {
  return allCareerClimbs
    .filter((climb) => climb.productPlan?.some((p) => p.code === planCode))
    .map((climb) => climb.id);
};

// Award types that read as a certificate to a climber. POK credentials are
// certificates too — the certificate report already groups them together.
export const CERTIFICATE_AWARD_TYPES = ["certificate", "pok"];

export const isCertificateAward = (award) =>
  CERTIFICATE_AWARD_TYPES.includes(award?.awardType);

/**
 * Certificates a climber can work toward on a page showing `visibleClimbs`,
 * paired with the climbs that count toward each.
 *
 * Contributing climbs come from `getClimbsForAward`, the same resolver that
 * decides when the award is actually earned, so a filter built on this can
 * never disagree with what grants the certificate. Certificates with nothing to
 * contribute here are dropped: an option that filters to an empty list is the
 * bug, not the fix.
 */
export const getCertificatesWithClimbs = (
  awards,
  visibleClimbs,
  allCareerClimbs,
  courses = [],
) => {
  const visibleIds = new Set((visibleClimbs || []).map((c) => String(c.id)));

  return (awards || [])
    .filter(isCertificateAward)
    .map((award) => {
      const climbIds = new Set(
        getClimbsForAward(award, allCareerClimbs || [], courses)
          .map(String)
          .filter((id) => visibleIds.has(id)),
      );
      return { award, climbIds };
    })
    .filter(({ climbIds }) => climbIds.size > 0)
    .sort((a, b) =>
      certificateLabel(a.award).localeCompare(certificateLabel(b.award)),
    );
};

// `name` is the CMS's internal label; `title` is what climbers are shown.
export const certificateLabel = (award) => award?.title || award?.name || "";
